import { useSelector } from "react-redux";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  downloadChargeFilePDF,
  getAllInvoicesForTransaction,
  selectTransactionInvoices,
  selectLoadingTransactionInvoices,
} from "./TransactionInvoicesTableSlice";
import { PLInvoiceChargeFileModel } from "services/api/cplapi/CPLAPI";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faMoneyBill1 } from "@fortawesome/pro-solid-svg-icons";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { PageDescription } from "_metronic/layout/core";
import { downloadFile } from "cpl-react-common/Helpers/FileHelpers";
import { selectGlobalSettings } from "app/modules/system/settings/ClientPortalSettingsSlice";

const TransactionInvoicesTable: React.FC = () => {
  const { transactionID } = useParams();
  const navigate = useNavigate();
  const thunkDispatch = useThunkDispatch();
  const transactionInvoices = useSelector(selectTransactionInvoices);
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const isLoading = useSelector(selectLoadingTransactionInvoices);
  const globalSettings = useSelector(selectGlobalSettings);
  const showRaiseCaseButton = globalSettings.showRaiseCasesButton && globalSettings.showCases;

  const columnHelper = createColumnHelper<PLInvoiceChargeFileModel>();
  const columnDefinition = [
    columnHelper.display({
      id: "icon",
      cell: () => (
        <div className="d-flex">
          <FontAwesomeIcon icon={faMoneyBill1} size="lg" className="cpl-brand-primary" />
        </div>
      ),
      enableHiding: false,
    }),
    columnHelper.accessor((row) => row.transactionDate, {
      id: "Transaction Date",
      cell: (info) => info.getValue()?.toCPLUserFriendlyDateString(),
      header: () => <>Transaction Date</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop ",
      },
    }),
    columnHelper.accessor((row) => row.description, {
      id: "Description",
      cell: (info) => info.getValue(),
      header: () => <>Description</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop ",
      },
    }),
    columnHelper.accessor((row) => row.description, {
      id: "Details",
      cell: (info) => (
        <>
          <>{info.row.original.transactionDate?.toCPLUserFriendlyDateString()}</>
          <div className="text-muted">{info.row.original.description}</div>
        </>
      ),
      header: () => <>Description</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "showTableCellOnMobile",
      },
    }),
    columnHelper.accessor((row) => row.invoiceAmount, {
      id: "Invoice Amount",
      cell: (info) => info.getValue()!.toGBPString(),
      header: () => <>Invoice Amount</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop currency-column-header",
        dataClassName: "text-end",
      },
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.yourShare, {
      id: "Your Share",
      cell: (info) => info.getValue(),
      header: () => <>Your Share</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop ",
      },
    }),
    columnHelper.accessor((row) => row.yourAmount, {
      id: "Your Amount",
      cell: (info) => info.getValue()!.toGBPString(),
      header: () => <>Your Amount</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop currency-column-header",
        dataClassName: "text-end",
      },
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.yourAmount, {
      id: "Amount",
      cell: (info) => (
        <>
          <div className="text-end">{info.getValue()!.toGBPString()}</div>
          <div className="text-end">of {info.row.original.invoiceAmount?.toGBPString()}</div>
          <div className="text-end text-muted">{info.row.original.yourShare}</div>
        </>
      ),
      header: () => <>Your Amount</>,
      meta: {
        columnClassName: "showTableCellOnMobile",
      },
      enableColumnFilter: false,
    }),
    columnHelper.display({
      id: "secondaryActions",
      cell: (props) => (
        <div className="d-flex justify-content-end">
          {(props.row.original.queryCaseID && (
            <div
              className="cpl-btn btn-sm cpl-btn-outline me-1"
              onClick={() => navigateToCase(props.row.original)}
              data-test-id={`invoice-charges-table__view-case-link-${props.row.original.chargeFileID}`}
            >
              View Case
            </div>
          )) ||
            (props.row.original.invoiceNumber != "" && props.row.original.yourAmount! > 0 && showRaiseCaseButton && (
              <div
                className="cpl-btn btn-sm cpl-btn-outline me-1"
                onClick={() => navigateToQuery(props.row.original)}
                data-test-id={`invoice-charges-table__raise-case-link-${props.row.original.chargeFileID}`}
              >
                Raise case
              </div>
            ))}
          {props.row.original.invoiceNumber !== null && (
            <div>
              <a
                className="cpl-btn btn-icon cpl-btn-outline"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadChargeFile(props.row.original.chargeFileID!);
                }}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Download Invoice"
                data-test-id={`invoice-charges-table__download-invoice-button-${props.row.original.chargeFileID}`}
                target="_blank"
                download
              >
                <FontAwesomeIcon icon={faDownload} />
              </a>
            </div>
          )}
        </div>
      ),
      enableHiding: false,
    }),
  ];

  async function downloadChargeFile(chargeFileID: string) {
    const file = await thunkDispatch(downloadChargeFilePDF(chargeFileID)).unwrap();
    downloadFile(file);
  }

  const navigateToQuery = (invoiceRow: PLInvoiceChargeFileModel) => {
    const path = `/Case/?charge_file_id=${invoiceRow.chargeFileID}&invoice_number=${invoiceRow.invoiceNumber}&property_id=${invoiceRow.propertyID}`;
    navigate(path);
  };

  const navigateToCase = (transactionRow: PLInvoiceChargeFileModel) => {
    const path = `/Case/${transactionRow.queryCaseID}`;
    navigate(path);
  };

  useEffect(() => {
    thunkDispatch(getAllInvoicesForTransaction({ transactionID: transactionID! }));
  }, [thunkDispatch, transactionID]);

  return (
    <>
      <PageDescription>{`${transactionInvoices.accountNumber} - ${transactionInvoices.address} - Invoice No. ${transactionInvoices.invoiceNumber}`}</PageDescription>
      <div className="card d-flex">
        <ActivityIndicator isBusy={isLoading}>
          <div className="card-body">
            <a ref={anchorRef} target="_blank" rel="noreferrer" className="file-preview-link" />
            <CPLTable
              data={transactionInvoices.invoiceCharges ?? []}
              columns={columnDefinition}
              classNames="cpl-table-nowrap-below-desktop"
            />
          </div>
        </ActivityIndicator>
      </div>
    </>
  );
};

export default TransactionInvoicesTable;
